import React from 'react';
import { useTranslation } from 'react-i18next';
import SeoHead from '../../components/SeoHead';
import type { SeoPageKey } from '../../config/seo';

export type LegalType = 'privacy' | 'terms' | 'cookies' | 'refund';

const TITLE_KEYS: Record<LegalType, string> = {
  privacy: 'footer.legalPrivacy',
  terms: 'footer.legalTerms',
  cookies: 'footer.legalCookies',
  refund: 'footer.legalRefund',
};

/** Section count per document type for intro + s1Title/s1Content ... sNTitle/sNContent */
const SECTION_COUNTS: Record<LegalType, number> = {
  privacy: 8,
  terms: 10,
  cookies: 5,
  refund: 7,
};

interface LegalPageProps {
  legalType: LegalType;
}

export default function LegalPage({ legalType }: LegalPageProps) {
  const { t } = useTranslation();
  const titleKey = TITLE_KEYS[legalType];
  const prefix = `legal.${legalType}`;
  const sectionCount = SECTION_COUNTS[legalType];
  const introKey = `${prefix}.intro`;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
      <SeoHead page={legalType as SeoPageKey} />
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
        {t('legal.lastUpdated')}: {t('legal.lastUpdatedDate')}
      </p>
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
        {t(titleKey)}
      </h1>
      <div className="prose dark:prose-invert prose-p:mb-4 text-gray-600 dark:text-gray-300">
        <p>{t(introKey)}</p>
        {Array.from({ length: sectionCount }, (_, i) => i + 1).map((n) => (
          <section key={n} className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mt-6 mb-2">
              {t(`${prefix}.s${n}Title`)}
            </h2>
            <p>{t(`${prefix}.s${n}Content`)}</p>
          </section>
        ))}
      </div>
    </div>
  );
}
